import { useState, useEffect } from 'react';
import { 
  X, Phone, User, Save, 
  ChevronRight, AlertTriangle, CheckCircle2, Check, Clock, 
  Calendar, ArrowRight, ArrowLeft, Search, ExternalLink, Loader2, UserCheck,
  Flame, Wrench, FileText, Shield
} from 'lucide-react';
import SimulateurMPAWidget from './SuperBatard/widgets/SimulateurMPAWidget';
import SimulateurRenoEnergieWidget from './SuperBatard/widgets/SimulateurRenoEnergieWidget';
import TravauxConfortWidget from './SuperBatard/widgets/TravauxConfortWidget';
import getApiUrl from '../utils/getApiUrl';

const API = `${getApiUrl()}/api`;

const authFetch = (url, options = {}) => {
  const token = localStorage.getItem("auth_token");
  return fetch(url, {
    ...options,
    credentials: "include",
    headers: { ...(options.headers || {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
};


const STEP_LABELS = {
  'init': 'Début',
  'autre_demande': 'Autre demande',
  1: 'Infos lead',
  2: 'Éligibilité MPA',
  3: 'Rénovation Énergétique',
  4: 'Travaux de confort',
  5: 'Finalisation'
};

const TOTAL_STEPS = 5;

// Extraire les groupes de décisions depuis l'arbre config MongoDB
// Cherche les noeuds parents dont les enfants de type 'choice' ont des target_etapes
const extractDecisionGroups = (node) => {
  if (!node) return [];
  const groups = [];
  const walk = (n) => {
    const allChoices = (n.children || []).filter(c => c.type === 'choice');
    const hasTargets = allChoices.some(c => c.target_etapes?.length > 0);
    // Exclure step3-rdv et step3-bloque (gérés par le choix de statut principal)
    const isMainStatut = n.id?.startsWith('step3-');
    // Un groupe de décision = au moins 2 choices dont au moins 1 a des target_etapes
    if (allChoices.length >= 2 && hasTargets && !isMainStatut) {
      groups.push({
        id: n.id,
        label: n.label,
        choices: allChoices.map(c => ({
          id: c.id,
          label: c.label,
          color: c.color,
          target_etapes: c.target_etapes || [],
        }))
      });
    }
    (n.children || []).forEach(walk);
  };
  walk(node);
  return groups;
};

// Extraire les nœuds de décision avec target_etapes depuis l'arbre config (pour étape principale)
const extractDecisionNodes = (node) => {
  if (!node) return {};
  const result = {};
  const walk = (n) => {
    if (n.target_etapes?.length > 0) {
      result[n.id] = { label: n.label, target_etapes: n.target_etapes };
    }
    (n.children || []).forEach(walk);
  };
  walk(node);
  return result;
};

const QualificationAppel = ({ isOpen, onClose, authUser, onLeadCreated, onOpenClientFiche, resumeClient = null, embedded = false, societeId = null, prefillLead = null }) => {
  const [step, setStep] = useState('init');
  const [statuts, setStatuts] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Télépro assigné
  const [telepros, setTelepros] = useState([]);
  const [selectedTelepro, setSelectedTelepro] = useState(null);
  
  // Row number du client en cours de qualification
  const [currentRowNumber, setCurrentRowNumber] = useState(null);
  const [savingProgress, setSavingProgress] = useState(false);

  // Recherche client existant
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [clientExistant, setClientExistant] = useState(null);
  const [showSearchResults, setShowSearchResults] = useState(false);
  
  // Infos client
  const [clientInfo, setClientInfo] = useState({
    nom: '', prenom: '', telephone: '', email: '', adresse: '', codePostal: '', ville: ''
  });
  
  // Données des widgets
  const [eligibiliteData, setEligibiliteData] = useState(null);
  const [renoEnergieData, setRenoEnergieData] = useState(null);
  const [travauxConfortData, setTravauxConfortData] = useState(null);
  
  // Statut final et notes
  const [statutFinal, setStatutFinal] = useState(null);
  const [dateRappel, setDateRappel] = useState('');
  const [heureRappel, setHeureRappel] = useState('');
  const [notes, setNotes] = useState('');
  const [autreDemande, setAutreDemande] = useState('');

  // Source du lead
  const [selectedSource, setSelectedSource] = useState('');

  // Interets multi-selection (etape 5)
  const [produitsInteresses, setProduitsInteresses] = useState([]);

  // Numéro de dossier (généré à l'étape 5)
  const [numeroDossier, setNumeroDossier] = useState('');

  // Protection juridique (tutelle/curatelle)
  const [protectionJuridique, setProtectionJuridique] = useState('non'); // 'non', 'tutelle', 'curatelle', 'ne_sait_pas'

  // Doublon detecte
  const [duplicateClient, setDuplicateClient] = useState(null);

  // Config appels entrants (depuis MongoDB)
  const [flowConfig, setFlowConfig] = useState(null);
  const [decisionGroups, setDecisionGroups] = useState([]);
  const [decisionsConfig, setDecisionsConfig] = useState({});  // {groupId: choiceId}

  useEffect(() => {
    const loadStatuts = async () => {
      try {
        const response = await authFetch(`${API}/qualification/statuts`);
        if (response.ok) setStatuts(await response.json());
      } catch (error) {
        console.error('Erreur chargement statuts:', error);
      }
    };
    const loadTelepros = async () => {
      try {
        const response = await authFetch(`${API}/qualification/telepros`);
        if (response.ok) {
          const data = await response.json();
          setTelepros(data.telepros || []);
          if (authUser?.email) {
            const match = (data.telepros || []).find(t => t.email === authUser.email);
            if (match) setSelectedTelepro(match);
          }
        }
      } catch (error) {
        console.error('Erreur chargement telepros:', error);
      }
    };
    if (isOpen) {
      loadStatuts();
      loadTelepros();

      // Charger la config appels entrants depuis MongoDB
      authFetch(`${API}/appels-entrants-config/appels-entrants-flow`)
        .then(r => r.ok ? r.json() : null)
        .then(data => {
          if (data?.flow) {
            setFlowConfig(data.flow);
            const groups = extractDecisionGroups(data.flow);
            setDecisionGroups(groups);
          }
        })
        .catch(() => {});

      // Mode pré-remplissage depuis Appel Sortant (lead décroché)
      if (prefillLead) {
        setStep('init');
        setClientInfo({
          nom: prefillLead.nom || '', prenom: prefillLead.prenom || '',
          telephone: prefillLead.telephone || '', email: prefillLead.email || '',
          adresse: prefillLead.adresse || '', codePostal: prefillLead.code_postal || prefillLead.codePostal || '',
          ville: prefillLead.ville || ''
        });
        setSelectedSource(prefillLead.source || '');
        setCurrentRowNumber(null);
        setSavingProgress(false);
        setEligibiliteData(null);
        setRenoEnergieData(null);
        setTravauxConfortData(null);
        setStatutFinal(null);
        setDateRappel('');
        setHeureRappel('');
        setNotes('');
        setAutreDemande('');
        setProduitsInteresses([]);
        // Le télépro sera sélectionné via prefillLead.selectedTelepro dans loadTelepros
        if (prefillLead.selectedTelepro) {
          setSelectedTelepro(prefillLead.selectedTelepro);
        }
      // Mode reprise : charger les données du client et reprendre à l'étape sauvegardée
      } else if (resumeClient) {
        setCurrentRowNumber(resumeClient.row_number);
        setClientExistant(resumeClient);
        setClientInfo({
          nom: resumeClient.nom || '', prenom: resumeClient.prenom || '',
          telephone: resumeClient.telephone || '', email: resumeClient.email || '',
          adresse: resumeClient.adresse || '', codePostal: resumeClient.code_postal || '',
          ville: resumeClient.ville || ''
        });
        // Charger la progression
        (async () => {
          try {
            const r = await authFetch(`${API}/qualification/client-progress/${resumeClient.row_number}`);
            if (r.ok) {
              const d = await r.json();
              const c = d.client || {};
              const progress = c.qualification_progress || {};
              if (progress.mpa) setEligibiliteData(progress.mpa);
              if (progress.reno_energie) setRenoEnergieData(progress.reno_energie);
              if (progress.travaux_confort) setTravauxConfortData(progress.travaux_confort);
              // Aller à l'étape sauvegardée
              const savedStep = progress.current_step || 1;
              setStep(savedStep);
            } else {
              setStep(1);
            }
          } catch (e) {
            console.error('Erreur chargement progression:', e);
            setStep(1);
          }
        })();
      } else {
        // Mode normal : reset complet
        setStep('init');
        setSearchQuery('');
        setSearchResults([]);
        setClientExistant(null);
        setShowSearchResults(false);
        setClientInfo({ nom: '', prenom: '', telephone: '', email: '', adresse: '', codePostal: '', ville: '' });
        setCurrentRowNumber(null);
        setSavingProgress(false);
        setEligibiliteData(null);
        setRenoEnergieData(null);
        setTravauxConfortData(null);
        setStatutFinal(null);
        setDateRappel('');
        setHeureRappel('');
        setNotes('');
        setAutreDemande('');
        setSelectedSource('');
        setProduitsInteresses([]);
      }
    }
  }, [isOpen, authUser?.email, resumeClient, prefillLead]);

  // Recherche automatique
  useEffect(() => {
    const searchClients = async () => {
      const query = searchQuery.trim();
      if (query.length < 3) { setSearchResults([]); setShowSearchResults(false); return; }
      setSearchLoading(true);
      try {
        const response = await authFetch(`${API}/qualification/recherche-client?q=${encodeURIComponent(query)}`);
        if (response.ok) {
          const data = await response.json();
          setSearchResults(data.clients || []);
          setShowSearchResults(data.clients?.length > 0);
        }
      } catch (error) { console.error('Erreur recherche client:', error); }
      finally { setSearchLoading(false); }
    };
    const timeoutId = setTimeout(searchClients, 500);
    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  const selectClientExistant = async (client) => {
    setClientExistant(client);
    setClientInfo({
      nom: client.nom || '', prenom: client.prenom || '',
      telephone: client.telephone || '', email: client.email || '',
      adresse: client.adresse || '', codePostal: client.code_postal || '',
      ville: client.ville || ''
    });
    setCurrentRowNumber(client.row_number);
    setShowSearchResults(false);

    // Charger les données existantes du client
    try {
      const r = await authFetch(`${API}/qualification/client-progress/${client.row_number}`);
      if (r.ok) {
        const d = await r.json();
        const c = d.client || {};
        const progress = c.qualification_progress || {};
        if (progress.mpa) setEligibiliteData(progress.mpa);
        if (progress.reno_energie) setRenoEnergieData(progress.reno_energie);
        if (progress.travaux_confort) setTravauxConfortData(progress.travaux_confort);
      }
    } catch (e) { console.error('Erreur chargement progression:', e); }
  };

  const ouvrirFicheClient = (client) => {
    if (onOpenClientFiche) { onOpenClientFiche(client.row_number); onClose(); }
  };

  const enregistrerAppelNegatif = async (statutCode, statutLabel) => {
    setSaving(true);
    try {
      const response = await authFetch(`${API}/qualification/appel-negatif`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          statut_code: statutCode, statut_label: statutLabel,
          notes: notes || autreDemande, telepro_email: selectedTelepro?.email || authUser?.email,
          telepro_nom: selectedTelepro?.name || authUser?.name, telephone_appele: clientInfo.telephone || null,
          source: selectedSource || 'appel_entrant'
        })
      });
      if (response.ok) { alert('Appel enregistre dans les statistiques'); onClose(); }
    } catch (error) { console.error('Erreur:', error); alert('Erreur lors de l\'enregistrement'); }
    finally { setSaving(false); }
  };

  const creerLead = async () => {
    if (!clientInfo.nom || !clientInfo.telephone) { alert('Le nom et le telephone sont obligatoires'); return; }
    // Determiner le statut effectif
    const effectiveStatut = statutFinal || (produitsInteresses.length > 0
      ? { code: produitsInteresses.join('+'), label: produitsInteresses.map(c => c === 'INT_HORS_PRIME' ? 'Hors Prime' : 'Reno Energie').join(' + ') }
      : null);
    if (!effectiveStatut && produitsInteresses.length === 0) { alert('Veuillez selectionner un statut ou un produit'); return; }
    setSaving(true);
    try {
      // 1. Enregistrer l'éligibilité dans l'historique
      if (currentRowNumber) {
        await authFetch(`${API}/pipeline/client/row_${currentRowNumber}/eligibilites`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            telepro_email: selectedTelepro?.email || authUser?.email,
            telepro_nom: selectedTelepro?.name || authUser?.name,
            mpa: eligibiliteData?.checked ? {
              resultat: eligibiliteData.resultat,
              zone: eligibiliteData.zone,
              nb_personnes: eligibiliteData.nbPersonnes,
              rfr: eligibiliteData.rfr,
              taux_prise_en_charge: eligibiliteData.tauxPriseEnCharge,
              enveloppe_max: eligibiliteData.enveloppeMax,
              montant_restant: eligibiliteData.montantRestant,
              type_bien: eligibiliteData.typeBien,
              statut_logement: eligibiliteData.statutLogement,
            } : {},
            reno_energie: renoEnergieData?.checked ? {
              categorie_mpr: renoEnergieData.categorieMPR,
              produits: (renoEnergieData.produitsSelectionnes || []).map(p => ({
                nom: p.nom || p.label || p,
                montant_cee: p.montantCEE || 0
              })),
              total_cee: renoEnergieData.totalCEE || 0
            } : {},
            travaux_supplementaires: travauxConfortData?.selectionnes?.length > 0 ? {
              selectionnes: travauxConfortData.selectionnes.map(t => ({
                label: t.label || t.nom || t,
                prix: t.prix || 0
              }))
            } : {},
            statut_final: (effectiveStatut || statutFinal)?.label || (effectiveStatut || statutFinal)?.code,
            notes
          })
        }).catch(e => console.error('Erreur sauvegarde eligibilite:', e));
      }

      // 2. Résoudre le routage depuis la config
      const configNodes = flowConfig ? extractDecisionNodes(flowConfig) : {};
      const configSecondaires = [];

      // Collecter les target_etapes de chaque décision choisie
      for (const group of decisionGroups) {
        const chosenId = decisionsConfig[group.id];
        if (chosenId) {
          const choice = group.choices.find(c => c.id === chosenId);
          if (choice?.target_etapes?.length > 0) {
            configSecondaires.push(...choice.target_etapes);
          }
        }
      }

      // Étape principale depuis la config (si statut RDV)
      let configPrincipal = null;
      if (effectiveStatut.code === 'RDV_MPA_ELIGIBLE' && configNodes['c-rdv-mpa']?.target_etapes?.[0]) {
        configPrincipal = configNodes['c-rdv-mpa'].target_etapes[0];
      } else if (effectiveStatut.code === 'RDV_HORS_MPA' && configNodes['c-rdv-hors']?.target_etapes?.[0]) {
        configPrincipal = configNodes['c-rdv-hors'].target_etapes[0];
      } else if (effectiveStatut.code.startsWith('BLOC_') && configNodes['fin-rappel']?.target_etapes?.[0]) {
        configPrincipal = configNodes['fin-rappel'].target_etapes[0];
      }

      // Transformer les décisions pour le backend (backward compat)
      const decisionsForBackend = {};
      for (const group of decisionGroups) {
        const chosenId = decisionsConfig[group.id];
        if (chosenId) {
          const choice = group.choices.find(c => c.id === chosenId);
          // Extraire la clé du groupe depuis l'ID (q-decision-energie → energie)
          const groupKey = group.id.replace('q-decision-', '').replace('q-', '');
          const choiceKey = choice?.id?.includes('accepte') ? 'accepte' : choice?.id?.includes('hesite') ? 'hesite' : 'refuse';
          decisionsForBackend[groupKey] = choiceKey;
        }
      }

      // 3. Créer le lead
      const response = await authFetch(`${API}/qualification/nouveau-lead`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          nom: clientInfo.nom, prenom: clientInfo.prenom,
          telephone: clientInfo.telephone, email: clientInfo.email,
          adresse: clientInfo.adresse, code_postal: clientInfo.codePostal,
          ville: clientInfo.ville,
          simulation_mpa: eligibiliteData?.checked ? eligibiliteData : null,
          reno_energie: renoEnergieData?.checked ? renoEnergieData : null,
          travaux_confort: travauxConfortData?.selectionnes?.length > 0 ? travauxConfortData : null,
          statut_code: effectiveStatut.code, statut_label: effectiveStatut.label,
          produits_interesses: eligibiliteData?.produitsInteresses || [],
          produits_interesses_step5: produitsInteresses,
          travaux_hors_mpa: eligibiliteData?.travauxHorsMPASelectionnes || [],
          date_rappel: dateRappel || null, heure_rappel: heureRappel || null,
          motif_rappel: effectiveStatut.code.startsWith('BLOC_') ? effectiveStatut.label : null,
          telepro_email: selectedTelepro?.email || authUser?.email,
          telepro_nom: selectedTelepro?.name || authUser?.name,
          source: selectedSource || 'appel_entrant', notes,
          existing_row_number: currentRowNumber,
          numero_dossier: numeroDossier || null,
          protection_juridique: protectionJuridique || 'non',
          decisions_travaux: decisionsForBackend,
          config_principal_etape: configPrincipal || null,
          config_secondaires: configSecondaires.length > 0 ? configSecondaires : null,
        })
      });
      if (response.ok) {
        const data = await response.json();
        // === DOUBLON DETECTE ===
        if (data.duplicate) {
          setDuplicateClient(data.existing_client);
          setSaving(false);
          return;
        }
        // Toujours fermer et rediriger vers la fiche si on a un row_number
        const rowNum = data.row_number || currentRowNumber;
        if (rowNum && onOpenClientFiche) {
          onOpenClientFiche(rowNum, { ...data.client_data, protection_juridique: protectionJuridique });
        } else {
          if (onLeadCreated) onLeadCreated(data);
          onClose();
        }
      }
    } catch (error) { console.error('Erreur:', error); alert('Erreur lors de la creation du lead'); }
    finally { setSaving(false); }
  };

  // Navigation helpers — sauvegarde progressive
  const goNext = async () => {
    if (step === 'init') { setStep(1); return; }
    if (typeof step !== 'number') return;

    setSavingProgress(true);
    try {
      const payload = { step, row_number: currentRowNumber };

      if (step === 1) {
        payload.client_info = clientInfo;
      } else if (step === 2) {
        payload.eligibilite_data = eligibiliteData || {};
      } else if (step === 3) {
        payload.reno_energie_data = renoEnergieData || {};
      } else if (step === 4) {
        payload.travaux_confort_data = travauxConfortData || {};
      }

      const r = await authFetch(`${API}/qualification/save-progress`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (r.ok) {
        const d = await r.json();
        if (d.row_number && !currentRowNumber) {
          setCurrentRowNumber(d.row_number);
        }
        if (step < TOTAL_STEPS) {
          const nextStep = step + 1;
          // Générer le numéro de dossier à l'entrée de l'étape 5
          if (nextStep === 5 && !numeroDossier) {
            const now = new Date();
            const pad = (n) => String(n).padStart(2, '0');
            const dateStr = `${now.getFullYear()}${pad(now.getMonth()+1)}${pad(now.getDate())}`;
            const rand = Math.random().toString(36).substring(2, 6).toUpperCase();
            setNumeroDossier(`SAN-${dateStr}-${rand}`);
          }
          setStep(nextStep);
        }
      } else {
        const err = await r.json().catch(() => ({}));
        console.error('Erreur sauvegarde:', err);
      }
    } catch (e) {
      console.error('Erreur sauvegarde progression:', e);
    }
    setSavingProgress(false);
  };
  const goBack = () => {
    if (step === 1) setStep('init');
    else if (step === 'autre_demande') setStep('init');
    else if (typeof step === 'number' && step > 1) setStep(step - 1);
  };

  const progressPercent = typeof step === 'number' ? (step / TOTAL_STEPS) * 100 : 0;

  if (!isOpen) return null;

  const content = (
    <>
        {/* Header */}
        <div className="bg-gradient-to-r from-emerald-600 to-teal-600 p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
              <Phone className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-white">Nouveau Lead</h2>
              <p className="text-sm text-white/70">
                {typeof step === 'number' 
                  ? `Etape ${step}/${TOTAL_STEPS} — ${STEP_LABELS[step]}`
                  : STEP_LABELS[step] || ''}
                {selectedTelepro && <span className="ml-2 text-white/50">| {selectedTelepro.name}</span>}
              </p>
            </div>
          </div>
          {!embedded && (
            <button onClick={onClose} className="text-white/70 hover:text-white" data-testid="qualification-close-btn">
              <X className="w-6 h-6" />
            </button>
          )}
        </div>
        
        {/* Progress bar */}
        <div className="h-1 bg-slate-800">
          <div className="h-full bg-emerald-500 transition-all duration-300" style={{ width: `${progressPercent}%` }} />
        </div>
        
        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          
          {/* === INIT: Oui / Non === */}
          {step === 'init' && (
            <div className="space-y-6" data-testid="step-init">
              {/* S&eacute;lecteur T&eacute;l&eacute;pro */}
              <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4" data-testid="telepro-selector">
                <label className="block text-sm text-blue-400 font-medium mb-2 flex items-center gap-2">
                  <UserCheck className="w-4 h-4" />T&eacute;l&eacute;pro en charge de cet appel
                </label>
                <select
                  value={selectedTelepro?.email || ''}
                  onChange={e => {
                    const tp = telepros.find(t => t.email === e.target.value);
                    setSelectedTelepro(tp || null);
                  }}
                  data-testid="select-telepro"
                  className="w-full px-4 py-3 bg-slate-900 border border-blue-500/50 rounded-lg text-white text-sm focus:border-blue-400 focus:outline-none">
                  <option value="">-- S&eacute;lectionner le t&eacute;l&eacute;pro --</option>
                  {telepros.map(tp => (
                    <option key={tp.email} value={tp.email}>
                      {tp.name} ({tp.email})
                    </option>
                  ))}
                </select>
                {selectedTelepro && (
                  <p className="text-xs text-blue-300 mt-1.5">
                    Assign&eacute; &agrave; : <span className="font-medium">{selectedTelepro.name}</span>
                  </p>
                )}
              </div>
              
              <h3 className="text-xl font-bold text-white text-center">L'appel concerne-t-il l'adaptation du logement ?</h3>
              <p className="text-center text-slate-400">MaPrimeAdapt', aides ANAH, travaux d'accessibilit&eacute;...</p>
              <div className="grid grid-cols-2 gap-4 max-w-xl mx-auto">
                <button onClick={() => setStep(1)} data-testid="btn-oui"
                  className="p-6 bg-emerald-500/10 border-2 border-emerald-500/50 rounded-xl hover:bg-emerald-500/20 transition-colors">
                  <CheckCircle2 className="w-12 h-12 text-emerald-400 mx-auto mb-3" />
                  <span className="text-lg font-bold text-emerald-400">OUI</span>
                  <p className="text-sm text-slate-400 mt-2">Continuer vers le test d'&eacute;ligibilit&eacute;</p>
                </button>
                <button onClick={() => setStep('autre_demande')} data-testid="btn-non"
                  className="p-6 bg-amber-500/10 border-2 border-amber-500/50 rounded-xl hover:bg-amber-500/20 transition-colors">
                  <AlertTriangle className="w-12 h-12 text-amber-400 mx-auto mb-3" />
                  <span className="text-lg font-bold text-amber-400">NON</span>
                  <p className="text-sm text-slate-400 mt-2">La demande concerne autre chose</p>
                </button>
              </div>
            </div>
          )}
          
          {/* === AUTRE DEMANDE === */}
          {step === 'autre_demande' && (
            <div className="space-y-6" data-testid="step-autre-demande">
              <h3 className="text-xl font-bold text-white text-center">La demande concerne autre chose</h3>
              <p className="text-center text-slate-400">V&eacute;rifiez si le client existe et enregistrez la demande</p>
              
              {/* Recherche client */}
              <SearchClientBlock searchQuery={searchQuery} setSearchQuery={setSearchQuery}
                searchResults={searchResults} showSearchResults={showSearchResults}
                searchLoading={searchLoading} clientExistant={clientExistant}
                setClientExistant={setClientExistant} selectClientExistant={selectClientExistant}
                ouvrirFicheClient={ouvrirFicheClient} variant="autre" />
              
              {/* Infos client si nouveau */}
              {!clientExistant && (
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4">
                  <h4 className="text-white font-bold mb-3 flex items-center gap-2">
                    <User className="w-4 h-4 text-slate-400" />Informations client (optionnel)
                  </h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    <InputField label="Nom" value={clientInfo.nom} onChange={v => setClientInfo(p => ({ ...p, nom: v }))} />
                    <InputField label="Prenom" value={clientInfo.prenom} onChange={v => setClientInfo(p => ({ ...p, prenom: v }))} />
                    <InputField label="Telephone" value={clientInfo.telephone} onChange={v => setClientInfo(p => ({ ...p, telephone: v }))} type="tel" placeholder="06 XX XX XX XX" />
                  </div>
                </div>
              )}
              
              {/* Source du lead */}
              <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4">
                <label className="block text-sm text-slate-400 mb-2 font-medium">Source du lead</label>
                <select
                  value={selectedSource}
                  onChange={e => setSelectedSource(e.target.value)}
                  data-testid="select-source-lead-autre"
                  className="w-full px-4 py-3 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm focus:border-blue-500 focus:outline-none"
                >
                  <option value="">-- Selectionner la source --</option>
                  {selectedSource && !(statuts?.sources_lead || []).some(s => s.code === selectedSource) && (
                    <option value={selectedSource}>{selectedSource}</option>
                  )}
                  {(statuts?.sources_lead || []).map(s => (
                    <option key={s.code} value={s.code}>{s.label}</option>
                  ))}
                </select>
              </div>

              <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4">
                <label className="block text-sm text-amber-400 mb-2 font-medium">Description de la demande *</label>
                <textarea value={autreDemande} onChange={e => setAutreDemande(e.target.value)}
                  className="w-full px-4 py-3 bg-slate-900 border border-slate-600 rounded-lg text-white placeholder:text-slate-500 focus:border-amber-500 focus:outline-none"
                  rows={3} placeholder="Ex: Le client demande des informations sur les panneaux solaires..." />
              </div>
              
              <div className="flex gap-4">
                <button onClick={() => setStep('init')} className="px-6 py-3 bg-slate-700 hover:bg-slate-600 text-white rounded-lg flex items-center gap-2">
                  <ArrowLeft className="w-4 h-4" />Retour
                </button>
                <button
                  onClick={async () => {
                    if (!autreDemande.trim()) { alert('Veuillez decrire la demande'); return; }
                    setSaving(true);
                    try {
                      if (clientExistant && clientExistant.row_number) {
                        const response = await authFetch(`${API}/qualification/autre-demande-existant`, {
                          method: 'POST', headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({ row_number: clientExistant.row_number, commentaire: autreDemande,
                            telepro_email: selectedTelepro?.email || authUser?.email, telepro_nom: selectedTelepro?.name || authUser?.name })
                        });
                        if (response.ok) { alert(`Fiche de ${clientExistant.nom} mise a jour`); onClose(); }
                        else { const err = await response.json(); alert('Erreur: ' + (err.detail || 'Erreur inconnue')); }
                      } else {
                        await enregistrerAppelNegatif('AUTRE_DEMANDE', 'Autre demande hors perimetre');
                      }
                    } catch (error) { console.error('Erreur:', error); alert('Erreur lors de l\'enregistrement'); }
                    finally { setSaving(false); }
                  }}
                  disabled={saving || !autreDemande.trim()}
                  className="flex-1 px-6 py-3 bg-amber-600 hover:bg-amber-500 disabled:bg-slate-600 text-white rounded-lg font-medium flex items-center justify-center gap-2">
                  {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <CheckCircle2 className="w-5 h-5" />}
                  {clientExistant ? 'Mettre a jour la fiche' : 'Enregistrer et fermer'}
                </button>
              </div>
              
              <div className="p-4 bg-emerald-500/10 border border-emerald-500/30 rounded-xl">
                <p className="text-sm text-slate-400 mb-3">Le client est aussi int&eacute;ress&eacute; par l'adaptation du logement ?</p>
                <button onClick={() => setStep(1)}
                  className="w-full px-4 py-3 bg-emerald-600/20 border border-emerald-500/50 text-emerald-400 rounded-lg hover:bg-emerald-600/30 flex items-center justify-center gap-2">
                  <CheckCircle2 className="w-5 h-5" />Continuer vers le test d'&eacute;ligibilit&eacute;<ArrowRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          )}
          
          {/* === ETAPE 1: Infos lead === */}
          {step === 1 && (
            <div className="space-y-6" data-testid="step-1-infos">
              <h3 className="text-xl font-bold text-white text-center">Informations du lead</h3>
              <p className="text-center text-slate-400">Recherchez un client existant ou cr&eacute;ez une nouvelle fiche</p>
              
              <SearchClientBlock searchQuery={searchQuery} setSearchQuery={setSearchQuery}
                searchResults={searchResults} showSearchResults={showSearchResults}
                searchLoading={searchLoading} clientExistant={clientExistant}
                setClientExistant={setClientExistant} selectClientExistant={selectClientExistant}
                ouvrirFicheClient={ouvrirFicheClient} variant="lead" />
              
              <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4">
                <h4 className="text-white font-bold mb-3 flex items-center gap-2">
                  <User className="w-4 h-4 text-blue-400" />
                  Informations client {clientExistant ? '(pré-remplies)' : '(minimum requis)'}
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <InputField label="Nom *" value={clientInfo.nom} onChange={v => setClientInfo(p => ({ ...p, nom: v }))}
                    highlight={!!clientExistant} placeholder="Nom du client" />
                  <InputField label="Prenom" value={clientInfo.prenom} onChange={v => setClientInfo(p => ({ ...p, prenom: v }))}
                    highlight={!!clientExistant} />
                  <InputField label="Telephone *" value={clientInfo.telephone} onChange={v => setClientInfo(p => ({ ...p, telephone: v }))}
                    highlight={!!clientExistant} type="tel" placeholder="06 XX XX XX XX" />
                  <InputField label="Email" value={clientInfo.email} onChange={v => setClientInfo(p => ({ ...p, email: v }))}
                    highlight={!!clientExistant} type="email" placeholder="email@exemple.fr" />
                  <InputField label="Code postal" value={clientInfo.codePostal} onChange={v => setClientInfo(p => ({ ...p, codePostal: v }))}
                    highlight={!!clientExistant} placeholder="75001" />
                  <InputField label="Ville" value={clientInfo.ville} onChange={v => setClientInfo(p => ({ ...p, ville: v }))}
                    highlight={!!clientExistant} />
                </div>
              </div>
              
              {/* Source du lead */}
              <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4">
                <label className="block text-sm text-slate-400 mb-2 font-medium">Source du lead</label>
                <select
                  value={selectedSource}
                  onChange={e => setSelectedSource(e.target.value)}
                  data-testid="select-source-lead"
                  className="w-full px-4 py-3 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm focus:border-blue-500 focus:outline-none"
                >
                  <option value="">-- Selectionner la source --</option>
                  {selectedSource && !(statuts?.sources_lead || []).some(s => s.code === selectedSource) && (
                    <option value={selectedSource}>{selectedSource}</option>
                  )}
                  {(statuts?.sources_lead || []).map(s => (
                    <option key={s.code} value={s.code}>{s.label}</option>
                  ))}
                </select>
              </div>
              
              <StepNavigation onBack={goBack} onNext={goNext}
                nextDisabled={!clientInfo.nom || !clientInfo.telephone}
                nextLabel="&Eacute;ligibilit&eacute; MPA" saving={savingProgress} />
            </div>
          )}
          
          {/* === ETAPE 2: Eligibilite MPA === */}
          {step === 2 && (
            <div className="space-y-6" data-testid="step-2-mpa">
              {/* Bandeau resume client */}
              <div className="p-3 bg-slate-800/50 border border-slate-700 rounded-lg flex items-center gap-3 text-sm">
                <User className="w-4 h-4 text-blue-400 shrink-0" />
                <span className="text-white font-medium">{clientInfo.nom} {clientInfo.prenom}</span>
                <span className="text-slate-500">|</span>
                <span className="text-slate-400">{clientInfo.telephone}</span>
                {clientInfo.email && <><span className="text-slate-500">|</span><span className="text-slate-400">{clientInfo.email}</span></>}
              </div>
              
              <SimulateurMPAWidget onEligibiliteChange={setEligibiliteData} initialData={eligibiliteData} collapsed={false} codePostal={clientInfo.codePostal} />
              
              {/* Resume eligibilite MPA */}
              {eligibiliteData?.checked && (
                <div className={`p-4 rounded-xl border ${
                  eligibiliteData.resultat?.includes('modeste') ? 'bg-emerald-500/10 border-emerald-500/30'
                  : eligibiliteData.resultat === 'non_eligible' ? 'bg-red-500/10 border-red-500/30'
                  : 'bg-amber-500/10 border-amber-500/30'
                }`} data-testid="mpa-resume">
                  <h4 className="text-sm font-bold text-white mb-2 flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4" />R&eacute;sum&eacute; &eacute;ligibilit&eacute; MPA
                  </h4>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                    <div>
                      <span className="text-slate-400 block">R&eacute;sultat</span>
                      <span className={`font-bold ${eligibiliteData.resultat?.includes('modeste') ? 'text-emerald-400' : 'text-red-400'}`}>
                        {eligibiliteData.resultat === 'tres_modeste' && 'Tr&egrave;s Modeste (70%)'}
                        {eligibiliteData.resultat === 'modeste' && 'Modeste (50%)'}
                        {eligibiliteData.resultat === 'non_eligible' && 'Non &eacute;ligible'}
                        {eligibiliteData.resultat?.includes('_conditionnel') && 'Sous condition'}
                        {!eligibiliteData.resultat && 'Non test&eacute;'}
                      </span>
                    </div>
                    {eligibiliteData.zone && (
                      <div>
                        <span className="text-slate-400 block">Zone</span>
                        <span className="text-white">{eligibiliteData.zone === 'idf' ? 'Ile-de-France' : 'Province'}</span>
                      </div>
                    )}
                    {eligibiliteData.typeBien && (
                      <div>
                        <span className="text-slate-400 block">Type bien</span>
                        <span className="text-white">{eligibiliteData.typeBien === 'maison' ? 'Maison' : 'Appartement'}</span>
                      </div>
                    )}
                    {eligibiliteData.statutLogement && (
                      <div>
                        <span className="text-slate-400 block">Statut</span>
                <span className="text-white">{eligibiliteData.statutLogement === 'proprietaire' ? 'Propri&eacute;taire' : 'Locataire'}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              <StepNavigation onBack={goBack} onNext={goNext} nextLabel="Renovation Energetique" saving={savingProgress}
                nextDisabled={!eligibiliteData?.checked} />
            </div>
          )}
          
          {/* === ETAPE 3: Renovation Energetique === */}
          {step === 3 && (
            <div className="space-y-6" data-testid="step-3-reno">
              {/* Bandeau resume MPA */}
              <MpaResumeBanner eligibiliteData={eligibiliteData} clientInfo={clientInfo} />
              
              <SimulateurRenoEnergieWidget
                mpaData={{ ...eligibiliteData, codePostal: clientInfo.codePostal }}
                onDataChange={setRenoEnergieData}
                initialData={renoEnergieData}
                collapsed={false}
              />
              
              <StepNavigation onBack={goBack} onNext={goNext} nextLabel="Travaux de confort" saving={savingProgress} />
            </div>
          )}
          
          {/* === ETAPE 4: Travaux de confort === */}
          {step === 4 && (
            <div className="space-y-6" data-testid="step-4-confort">
              {/* Bandeau resume */}
              <MpaResumeBanner eligibiliteData={eligibiliteData} clientInfo={clientInfo} />
              
              {/* Resume reno energie si des produits ont ete selectionnes */}
              {renoEnergieData?.produitsSelectionnes?.length > 0 && (
                <div className="p-3 bg-orange-500/10 border border-orange-500/30 rounded-lg text-sm flex items-center gap-2">
                  <Flame className="w-4 h-4 text-orange-400 shrink-0" />
                  <span className="text-orange-300 font-medium">
                    {renoEnergieData.produitsSelectionnes.length} produit(s) r&eacute;no &eacute;nergie s&eacute;lectionn&eacute;(s)
                  </span>
                  {renoEnergieData.categorieMPR && (
                    <span className="text-slate-400 ml-2">| Cat. {renoEnergieData.categorieMPR}</span>
                  )}
                </div>
              )}
              
              <TravauxConfortWidget onDataChange={setTravauxConfortData} initialData={travauxConfortData} collapsed={false} />
              
              <StepNavigation onBack={goBack} onNext={goNext} nextLabel="Finalisation" saving={savingProgress} />
            </div>
          )}
          
          {/* === ETAPE 5: Finalisation === */}
          {step === 5 && (
            <div className="space-y-5" data-testid="step-5-finalisation">
              
              {/* === RECAPITULATIF COMPLET === */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                
                {/* Card Client */}
                <div className="bg-slate-800/60 border border-slate-700 rounded-xl p-4">
                  <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                    <User className="w-3.5 h-3.5" />Client
                  </h4>
                  <p className="text-white font-bold text-lg">{clientInfo.nom} {clientInfo.prenom}</p>
                  <div className="mt-2 space-y-1 text-sm">
                    <p className="text-slate-300 flex items-center gap-2"><Phone className="w-3.5 h-3.5 text-blue-400" />{clientInfo.telephone || 'Non renseigne'}</p>
                    {clientInfo.email && <p className="text-slate-400">{clientInfo.email}</p>}
                    {(clientInfo.codePostal || clientInfo.ville) && (
                      <p className="text-slate-400">{clientInfo.codePostal} {clientInfo.ville}</p>
                    )}
                  </div>
                  {selectedTelepro && (
                    <div className="mt-3 pt-3 border-t border-slate-700/50">
                      <p className="text-xs text-slate-500">Telepro</p>
                      <p className="text-sm text-blue-400 font-medium">{selectedTelepro.name}</p>
                    </div>
                  )}
                </div>
                
                {/* Card MPA */}
                <div className={`rounded-xl p-4 border ${
                  eligibiliteData?.checked 
                    ? eligibiliteData.resultat === 'tres_modeste' ? 'bg-emerald-500/10 border-emerald-500/30'
                      : eligibiliteData.resultat === 'modeste' ? 'bg-emerald-500/10 border-emerald-500/30'
                      : eligibiliteData.resultat === 'non_eligible' ? 'bg-red-500/10 border-red-500/30'
                      : 'bg-amber-500/10 border-amber-500/30'
                    : 'bg-slate-800/60 border-slate-700'
                }`}>
                  <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                    <CheckCircle2 className="w-3.5 h-3.5" />Eligibilite MPA
                  </h4>
                  {eligibiliteData?.checked ? (
                    <div>
                      <p className={`text-lg font-bold ${
                        eligibiliteData.resultat?.includes('modeste') ? 'text-emerald-400' : 'text-red-400'
                      }`}>
                        {eligibiliteData.resultat === 'tres_modeste' && 'Tres Modeste (70%)'}
                        {eligibiliteData.resultat === 'modeste' && 'Modeste (50%)'}
                        {eligibiliteData.resultat === 'non_eligible' && 'Non eligible'}
                        {eligibiliteData.resultat?.includes('_conditionnel') && 'Sous condition'}
                        {!['tres_modeste','modeste','non_eligible'].includes(eligibiliteData.resultat) && !eligibiliteData.resultat?.includes('_conditionnel') && 'Non teste'}
                      </p>
                      <div className="mt-2 grid grid-cols-2 gap-2 text-sm">
                        {eligibiliteData.zone && <div><span className="text-slate-500 text-xs">Zone</span><p className="text-slate-300">{eligibiliteData.zone === 'idf' ? 'Ile-de-France' : 'Province'}</p></div>}
                        {eligibiliteData.typeBien && <div><span className="text-slate-500 text-xs">Type</span><p className="text-slate-300">{eligibiliteData.typeBien === 'maison' ? 'Maison' : 'Appartement'}</p></div>}
                        {eligibiliteData.statutLogement && <div><span className="text-slate-500 text-xs">Statut</span><p className="text-slate-300">{eligibiliteData.statutLogement === 'proprietaire' ? 'Proprietaire' : 'Locataire'}</p></div>}
                        {eligibiliteData.nbPersonnes && <div><span className="text-slate-500 text-xs">Foyer</span><p className="text-slate-300">{eligibiliteData.nbPersonnes} pers.</p></div>}
                      </div>
                    </div>
                  ) : (
                    <p className="text-slate-500 italic">Non teste</p>
                  )}
                </div>
                
                {/* Card Reno Energie */}
                <div className={`rounded-xl p-4 border ${
                  renoEnergieData?.produitsSelectionnes?.length > 0 ? 'bg-orange-500/10 border-orange-500/30' : 'bg-slate-800/60 border-slate-700'
                }`}>
                  <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                    <Flame className="w-3.5 h-3.5" />Renovation energetique
                  </h4>
                  {renoEnergieData?.produitsSelectionnes?.length > 0 ? (
                    <div>
                      {renoEnergieData.categorieMPR && (
                        <p className="text-orange-400 font-bold mb-2">
                          Categorie {renoEnergieData.categorieMPR.charAt(0).toUpperCase() + renoEnergieData.categorieMPR.slice(1)}
                        </p>
                      )}
                      <div className="space-y-1">
                        {renoEnergieData.produitsSelectionnes.map((p, i) => (
                          <div key={i} className="flex items-center justify-between text-sm">
                            <span className="text-slate-300">{p.nom || p.label || p}</span>
                            {p.montantCEE > 0 && <span className="text-orange-400 font-medium">{p.montantCEE.toLocaleString('fr-FR')} EUR CEE</span>}
                          </div>
                        ))}
                      </div>
                      {renoEnergieData.totalCEE > 0 && (
                        <div className="mt-2 pt-2 border-t border-orange-500/20 flex justify-between text-sm">
                          <span className="text-slate-400">Total CEE estime</span>
                          <span className="text-orange-400 font-bold">{renoEnergieData.totalCEE.toLocaleString('fr-FR')} EUR</span>
                        </div>
                      )}
                    </div>
                  ) : (
                    <p className="text-slate-500 italic">Aucun produit selectionne</p>
                  )}
                </div>
                
                {/* Card Travaux Confort */}
                <div className={`rounded-xl p-4 border ${
                  travauxConfortData?.selectionnes?.length > 0 ? 'bg-purple-500/10 border-purple-500/30' : 'bg-slate-800/60 border-slate-700'
                }`}>
                  <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                    <Wrench className="w-3.5 h-3.5" />Travaux de confort
                  </h4>
                  {travauxConfortData?.selectionnes?.length > 0 ? (
                    <div className="space-y-1">
                      {travauxConfortData.selectionnes.map((t, i) => (
                        <div key={i} className="flex items-center justify-between text-sm">
                          <span className="text-slate-300">{t.label || t.nom || t}</span>
                          {t.prix > 0 && <span className="text-purple-400">{t.prix.toLocaleString('fr-FR')} EUR</span>}
                        </div>
                      ))}
                      {travauxConfortData.totalEstime > 0 && (
                        <div className="mt-2 pt-2 border-t border-purple-500/20 flex justify-between text-sm">
                          <span className="text-slate-400">Total estime</span>
                          <span className="text-purple-400 font-bold">{travauxConfortData.totalEstime.toLocaleString('fr-FR')} EUR</span>
                        </div>
                      )}
                    </div>
                  ) : (
                    <p className="text-slate-500 italic">Aucun travail selectionne</p>
                  )}
                </div>
              </div>
              
              {/* === PROTECTION JURIDIQUE (tutelle/curatelle) === */}
              <div className="bg-slate-800/60 border border-slate-700 rounded-xl p-5" data-testid="section-protection-juridique">
                <h4 className="text-sm font-bold text-slate-300 uppercase tracking-wider mb-4 flex items-center gap-2">
                  <Shield className="w-4 h-4 text-blue-400" /> Mesure de protection juridique
                </h4>
                <p className="text-xs text-slate-400 mb-3">Le bénéficiaire est-il sous mesure de protection ?</p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {[
                    { value: 'non', label: 'Non', color: 'emerald' },
                    { value: 'tutelle', label: 'Oui — Tutelle', color: 'red' },
                    { value: 'curatelle', label: 'Oui — Curatelle', color: 'red' },
                    { value: 'ne_sait_pas', label: 'Ne sait pas', color: 'amber' },
                  ].map(opt => (
                    <button
                      key={opt.value}
                      onClick={() => setProtectionJuridique(opt.value)}
                      data-testid={`protection-${opt.value}`}
                      className={`px-3 py-2.5 rounded-lg text-sm font-medium transition-all border ${
                        protectionJuridique === opt.value
                          ? opt.color === 'emerald' ? 'bg-emerald-500/20 border-emerald-500 text-emerald-400'
                            : opt.color === 'red' ? 'bg-red-500/20 border-red-500 text-red-400'
                            : 'bg-amber-500/20 border-amber-500 text-amber-400'
                          : 'bg-slate-900 border-slate-600 text-slate-400 hover:border-slate-500'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
                {(protectionJuridique === 'tutelle' || protectionJuridique === 'curatelle') && (
                  <div className="mt-3 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                    <p className="text-red-400 text-sm font-medium">
                      Une personne de confiance ({protectionJuridique === 'tutelle' ? 'tuteur' : 'curateur'}) devra être renseignée sur la fiche client avant la prise de RDV.
                    </p>
                  </div>
                )}
              </div>

              {/* === NUMÉRO DE DOSSIER — Script Télépro === */}
              <div className="bg-amber-500/10 border-2 border-amber-500/40 rounded-xl p-5" data-testid="section-numero-dossier">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-sm font-bold text-amber-400 uppercase tracking-wider flex items-center gap-2">
                    <FileText className="w-4 h-4" /> Numéro de dossier à communiquer
                  </h4>
                  <div className="px-4 py-2 bg-slate-900 border-2 border-amber-400 rounded-lg">
                    <span className="text-amber-300 font-mono font-bold text-xl tracking-widest" data-testid="numero-dossier-value">{numeroDossier}</span>
                  </div>
                </div>
                <div className="bg-slate-900/80 rounded-lg p-4 border border-slate-700">
                  <p className="text-xs text-slate-500 uppercase font-semibold mb-2">Script à lire au client :</p>
                  <p className="text-slate-200 text-sm leading-relaxed italic">
                    « Monsieur/Madame <span className="text-white font-semibold not-italic">{clientInfo.nom}</span>, votre dossier est bien enregistré sous le numéro <span className="text-amber-300 font-bold not-italic">{numeroDossier}</span>. Je vous demande de bien le noter.
                  </p>
                  <p className="text-slate-200 text-sm leading-relaxed italic mt-2">
                    C'est important : d'ici notre visite technique, il est possible que vous soyez contacté par téléphone ou à domicile par d'autres sociétés qui vous proposeront des services similaires. <span className="text-amber-300 font-semibold not-italic">Ne donnez suite à aucune sollicitation.</span> Votre dossier est déjà en cours de traitement chez nous et tout engagement parallèle pourrait compromettre votre éligibilité aux aides.
                  </p>
                  <p className="text-slate-200 text-sm leading-relaxed italic mt-2">
                    Le jour de la visite, notre technicien vous confirmera ce numéro de dossier. Si quelqu'un se présente sans le connaître — que ce soit par téléphone ou à votre porte — il ne fait pas partie de notre équipe. »
                  </p>
                </div>
              </div>

              {/* === CHOIX DU STATUT === */}
              <div className="space-y-3">
                <h4 className="text-sm font-semibold text-white flex items-center gap-2">
                  Quelle suite donner a ce lead ?
                </h4>
                
                {/* RDV - Positif */}
                <div className={`rounded-xl border-2 transition-all ${
                  statutFinal?.code?.startsWith('RDV_') ? 'border-emerald-500 bg-emerald-500/10' : 'border-slate-700/50 bg-slate-800/30'
                }`}>
                  <div className="p-3 flex items-center gap-2 border-b border-slate-700/30">
                    <Calendar className="w-4 h-4 text-emerald-400" />
                    <span className="text-sm font-medium text-emerald-400">Prendre RDV</span>
                  </div>
                  <div className="p-3 grid grid-cols-2 gap-2">
                    {(statuts?.rdv || [
                      { code: 'RDV_MPA_ELIGIBLE', label: 'RDV MPA Eligible', desc: 'Client eligible MPA' },
                      { code: 'RDV_HORS_MPA', label: 'RDV Hors MPA', desc: 'Travaux hors prime' }
                    ]).filter(s => s.actif !== false).map(s => (
                      <button key={s.code} onClick={() => setStatutFinal(s)} data-testid={`statut-${s.code}`}
                        className={`p-3 rounded-lg border text-left transition-all ${
                          statutFinal?.code === s.code 
                            ? 'bg-emerald-500/20 border-emerald-500 ring-1 ring-emerald-500/50' 
                            : 'bg-slate-900/50 border-slate-700 hover:border-emerald-500/50'
                        }`}>
                        <span className={`font-medium text-sm ${statutFinal?.code === s.code ? 'text-emerald-400' : 'text-white'}`}>{s.label}</span>
                        <p className="text-xs text-slate-500 mt-0.5">{s.desc}</p>
                      </button>
                    ))}
                  </div>
                </div>
                
                {/* Interesse autres produits — complementaire, multi-selection */}
                <div className={`rounded-xl border-2 transition-all ${
                  produitsInteresses.length > 0 ? 'border-violet-500 bg-violet-500/10' : 'border-slate-700/50 bg-slate-800/30'
                }`}>
                  <div className="p-3 flex items-center gap-2 border-b border-slate-700/30">
                    <Wrench className="w-4 h-4 text-violet-400" />
                    <span className="text-sm font-medium text-violet-400">Interesse autres produits</span>
                    {produitsInteresses.length > 0 && (
                      <span className="ml-auto text-xs bg-violet-500/30 text-violet-300 px-2 py-0.5 rounded-full">{produitsInteresses.length} selectionne(s)</span>
                    )}
                  </div>
                  <div className="p-3 grid grid-cols-2 gap-2">
                    {(statuts?.interesses || [
                      { code: 'INT_HORS_PRIME', label: 'Travaux Hors Prime', desc: 'Sans aide financiere' },
                      { code: 'INT_RENO_ENERGIE', label: 'Reno Energie', desc: 'CEE / MaPrimeRenov' }
                    ]).filter(s => s.actif !== false).map(s => {
                      const selected = produitsInteresses.includes(s.code);
                      return (
                        <button key={s.code} data-testid={`statut-${s.code}`}
                          onClick={() => {
                            setProduitsInteresses(prev =>
                              selected ? prev.filter(c => c !== s.code) : [...prev, s.code]
                            );
                          }}
                          className={`p-3 rounded-lg border text-left transition-all ${
                            selected
                              ? 'bg-violet-500/20 border-violet-500 ring-1 ring-violet-500/50'
                              : 'bg-slate-900/50 border-slate-700 hover:border-violet-500/50'
                          }`}>
                          <div className="flex items-center gap-2">
                            <div className={`w-4 h-4 rounded border-2 flex items-center justify-center transition-colors ${
                              selected ? 'bg-violet-500 border-violet-500' : 'border-slate-500'
                            }`}>
                              {selected && <Check className="w-3 h-3 text-white" />}
                            </div>
                            <span className={`font-medium text-sm ${selected ? 'text-violet-400' : 'text-white'}`}>{s.label}</span>
                          </div>
                          <p className="text-xs text-slate-500 mt-0.5 ml-6">{s.desc}</p>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* === DECISIONS TRAVAUX SECONDAIRES (depuis config MongoDB) === */}
                {decisionGroups.length > 0 && (
                  <div className="rounded-xl border-2 border-cyan-500/40 bg-cyan-500/5 p-3 space-y-3" data-testid="decisions-travaux-section">
                    <div className="flex items-center gap-2 pb-2 border-b border-cyan-500/20">
                      <ArrowRight className="w-4 h-4 text-cyan-400" />
                      <span className="text-sm font-medium text-cyan-400">Decisions workflows secondaires</span>
                      <span className="ml-auto text-[10px] text-cyan-500/60 font-mono">config MongoDB</span>
                    </div>

                    {decisionGroups.map(group => (
                      <div key={group.id} data-testid={`decision-group-${group.id}`}>
                        <p className="text-xs text-slate-400 mb-1.5">{group.label}</p>
                        <div className={`grid gap-2`} style={{ gridTemplateColumns: `repeat(${Math.min(group.choices.length, 3)}, 1fr)` }}>
                          {group.choices.map(choice => {
                            const isSelected = decisionsConfig[group.id] === choice.id;
                            const color = choice.color === 'green' || choice.id?.includes('accepte') ? 'emerald'
                              : choice.color === 'orange' || choice.id?.includes('hesite') ? 'amber'
                              : 'red';
                            return (
                              <button key={choice.id}
                                onClick={() => setDecisionsConfig(p => ({
                                  ...p,
                                  [group.id]: p[group.id] === choice.id ? undefined : choice.id
                                }))}
                                data-testid={`decision-${choice.id}`}
                                className={`px-3 py-2 rounded-lg border text-xs font-medium transition-all ${
                                  isSelected
                                    ? color === 'emerald' ? 'bg-emerald-500/20 border-emerald-500 text-emerald-400'
                                      : color === 'amber' ? 'bg-amber-500/20 border-amber-500 text-amber-400'
                                      : 'bg-red-500/20 border-red-500 text-red-400'
                                    : 'bg-slate-900/50 border-slate-700 text-slate-400 hover:border-slate-500'
                                }`}>
                                {choice.label}
                                {choice.target_etapes?.length > 0 && (
                                  <span className="block text-[9px] mt-0.5 opacity-60 font-mono">
                                    {choice.target_etapes.map(t => t.etape_nom || t.etape_id).join(', ')}
                                  </span>
                                )}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Bloque / A rappeler */}
                <div className={`rounded-xl border-2 transition-all ${
                  statutFinal?.code?.startsWith('BLOC_') ? 'border-amber-500 bg-amber-500/10' : 'border-slate-700/50 bg-slate-800/30'
                }`}>
                  <div className="p-3 flex items-center gap-2 border-b border-slate-700/30">
                    <Clock className="w-4 h-4 text-amber-400" />
                    <span className="text-sm font-medium text-amber-400">Bloque / A rappeler</span>
                  </div>
                  <div className="p-3 grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {statuts?.bloques?.filter(s => s.actif !== false).map(statut => (
                      <button key={statut.code} onClick={() => setStatutFinal({ code: statut.code, label: statut.label })}
                        data-testid={`statut-${statut.code}`}
                        className={`p-2.5 rounded-lg border text-left transition-all ${
                          statutFinal?.code === statut.code 
                            ? 'bg-amber-500/20 border-amber-500 ring-1 ring-amber-500/50' 
                            : 'bg-slate-900/50 border-slate-700 hover:border-amber-500/50'
                        }`}>
                        <span className={`font-medium text-xs ${statutFinal?.code === statut.code ? 'text-amber-400' : 'text-white'}`}>{statut.label}</span>
                      </button>
                    ))}
                  </div>
                  {statutFinal?.code?.startsWith('BLOC_') && (
                    <div className="px-3 pb-3 grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs text-amber-400/70 mb-1">Date de rappel</label>
                        <input type="date" value={dateRappel} onChange={e => setDateRappel(e.target.value)}
                          className="w-full px-3 py-2 bg-slate-900 border border-amber-500/30 rounded-lg text-white text-sm focus:border-amber-400 focus:outline-none" />
                      </div>
                      <div>
                        <label className="block text-xs text-amber-400/70 mb-1">Heure de rappel</label>
                        <input type="time" value={heureRappel} onChange={e => setHeureRappel(e.target.value)}
                          className="w-full px-3 py-2 bg-slate-900 border border-amber-500/30 rounded-lg text-white text-sm focus:border-amber-400 focus:outline-none" />
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Negatif / Perdu */}
                <div className={`rounded-xl border-2 transition-all ${
                  statutFinal?.code?.startsWith('NEG_') ? 'border-red-500 bg-red-500/10' : 'border-slate-700/50 bg-slate-800/30'
                }`}>
                  <div className="p-3 flex items-center gap-2 border-b border-slate-700/30">
                    <X className="w-4 h-4 text-red-400" />
                    <span className="text-sm font-medium text-red-400">Negatif / Perdu</span>
                  </div>
                  <div className="p-3 grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {statuts?.negatifs?.filter(s => s.actif !== false && !['NEG_FAUX_NUMERO','NEG_NRP','NEG_MESSAGERIE','NEG_RACCROCHE'].includes(s.code)).map(statut => (
                      <button key={statut.code} onClick={() => setStatutFinal({ code: statut.code, label: statut.label })}
                        data-testid={`statut-${statut.code}`}
                        className={`p-2.5 rounded-lg border text-left transition-all ${
                          statutFinal?.code === statut.code 
                            ? 'bg-red-500/20 border-red-500 ring-1 ring-red-500/50' 
                            : 'bg-slate-900/50 border-slate-700 hover:border-red-500/50'
                        }`}>
                        <span className={`font-medium text-xs ${statutFinal?.code === statut.code ? 'text-red-400' : 'text-white'}`}>{statut.label}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              
              {/* === NOTES === */}
              <div className="bg-slate-800/30 border border-slate-700 rounded-xl p-4">
                <label className="block text-sm text-slate-400 mb-2">Notes (optionnel)</label>
                <textarea value={notes} onChange={e => setNotes(e.target.value)}
                  className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm focus:border-blue-500 focus:outline-none"
                  rows={2} placeholder="Informations complementaires..." />
              </div>
              
              {/* === BOUTONS === */}
              <div className="flex gap-4">
                <button onClick={goBack} className="px-6 py-3 bg-slate-700 hover:bg-slate-600 text-white rounded-lg flex items-center gap-2">
                  <ArrowLeft className="w-4 h-4" />Retour
                </button>
                <button onClick={creerLead} disabled={saving || (!statutFinal && produitsInteresses.length === 0)} data-testid="btn-creer-lead"
                  className={`flex-1 px-6 py-3 rounded-lg font-medium flex items-center justify-center gap-2 transition-all ${
                    (statutFinal || produitsInteresses.length > 0)
                      ? statutFinal?.code?.startsWith('RDV_') ? 'bg-emerald-600 hover:bg-emerald-500 text-white'
                        : statutFinal?.code?.startsWith('BLOC_') ? 'bg-amber-600 hover:bg-amber-500 text-white'
                        : statutFinal?.code?.startsWith('NEG_') ? 'bg-red-600 hover:bg-red-500 text-white'
                        : 'bg-violet-600 hover:bg-violet-500 text-white'
                      : 'bg-slate-600 text-slate-400 cursor-not-allowed'
                  }`}>
                  {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
                  {statutFinal?.code?.startsWith('RDV_') ? 'Creer le lead + Planifier RDV'
                    : statutFinal?.code?.startsWith('BLOC_') ? 'Creer le lead (a rappeler)'
                    : statutFinal?.code?.startsWith('NEG_') ? 'Enregistrer (negatif)'
                    : (statutFinal || produitsInteresses.length > 0) ? 'Creer le lead'
                    : 'Selectionnez un statut'}
                </button>
              </div>
            </div>
          )}
        </div>

    {/* === MODAL DOUBLON DETECTE === */}
    {duplicateClient && (
      <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[60] flex items-center justify-center p-4" data-testid="duplicate-modal">
        <div className="bg-slate-900 border border-amber-500/50 rounded-2xl w-full max-w-md p-6 shadow-2xl">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-amber-500/20 rounded-full flex items-center justify-center flex-shrink-0">
              <AlertTriangle className="w-5 h-5 text-amber-400" />
            </div>
            <h3 className="text-lg font-bold text-amber-400">Doublon detecte</h3>
          </div>
          <p className="text-sm text-slate-300 mb-4">
            Un client avec ce numero de telephone existe deja dans la base :
          </p>
          <div className="bg-slate-800/60 border border-slate-700 rounded-xl p-4 mb-4 space-y-2">
            <div className="flex justify-between">
              <span className="text-xs text-slate-500">Nom</span>
              <span className="text-sm text-white font-medium">{duplicateClient.nom} {duplicateClient.prenom}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-xs text-slate-500">Telephone</span>
              <span className="text-sm text-white">{duplicateClient.telephone}</span>
            </div>
            {duplicateClient.dpt && (
              <div className="flex justify-between">
                <span className="text-xs text-slate-500">Departement</span>
                <span className="text-sm text-white">{duplicateClient.dpt}</span>
              </div>
            )}
            {duplicateClient.etape_nom && (
              <div className="flex justify-between">
                <span className="text-xs text-slate-500">Etape</span>
                <span className="text-sm text-emerald-400">{duplicateClient.etape_nom}</span>
              </div>
            )}
            {duplicateClient.telepro && (
              <div className="flex justify-between">
                <span className="text-xs text-slate-500">Telepro</span>
                <span className="text-sm text-slate-300">{duplicateClient.telepro}</span>
              </div>
            )}
          </div>
          <div className="flex gap-3">
            <button
              data-testid="duplicate-cancel-btn"
              onClick={() => setDuplicateClient(null)}
              className="flex-1 px-4 py-2.5 bg-slate-700 hover:bg-slate-600 text-white rounded-lg text-sm font-medium transition-colors"
            >
              Fermer
            </button>
            {duplicateClient.row_number && (
              <button
                data-testid="duplicate-open-fiche-btn"
                onClick={() => {
                  setDuplicateClient(null);
                  if (onOpenClientFiche) {
                    onOpenClientFiche(duplicateClient.row_number, duplicateClient);
                  }
                  onClose();
                }}
                className="flex-1 px-4 py-2.5 bg-amber-600 hover:bg-amber-500 text-white rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-colors"
              >
                <ExternalLink className="w-4 h-4" />Voir la fiche
              </button>
            )}
          </div>
        </div>
      </div>
    )}
    </>
  );

  if (embedded) {
    return (
      <div className="bg-slate-900 border border-slate-700 rounded-2xl overflow-hidden flex flex-col">
        {content}
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {content}
      </div>
    </div>
  );
};

// === Sub-components ===

const InputField = ({ label, value, onChange, type = 'text', placeholder, highlight = false }) => (
  <div>
    <label className="block text-xs text-slate-400 mb-1">{label}</label>
    <input type={type} value={value} onChange={e => onChange(e.target.value)}
      className={`w-full px-3 py-2 bg-slate-900 border rounded-lg text-white text-sm ${highlight ? 'border-emerald-500/50' : 'border-slate-600'}`}
      placeholder={placeholder || label.replace(' *', '')} />
  </div>
);

const StepNavigation = ({ onBack, onNext, nextDisabled = false, nextLabel = 'Suivant', saving = false }) => (
  <div className="flex gap-4">
    <button onClick={onBack} className="px-6 py-3 bg-slate-700 hover:bg-slate-600 text-white rounded-lg flex items-center gap-2" data-testid="btn-back">
      <ArrowLeft className="w-4 h-4" />Retour
    </button>
    <button onClick={onNext} disabled={nextDisabled || saving} data-testid="btn-next"
      className="flex-1 px-6 py-3 bg-emerald-600 hover:bg-emerald-500 disabled:bg-slate-600 text-white rounded-lg font-medium flex items-center justify-center gap-2">
      {saving ? <><Loader2 className="w-5 h-5 animate-spin" />Sauvegarde...</> : <>{nextLabel}<ArrowRight className="w-5 h-5" /></>}
    </button>
  </div>
);

const MpaResumeBanner = ({ eligibiliteData, clientInfo }) => (
  <div className="p-3 bg-slate-800/50 border border-slate-700 rounded-lg text-sm flex items-center gap-3 flex-wrap">
    <span className="text-white font-medium">{clientInfo.nom} {clientInfo.prenom}</span>
    {eligibiliteData?.checked && (
      <>
        <span className="text-slate-500">|</span>
        <span className={eligibiliteData.resultat?.includes('modeste') ? 'text-emerald-400' : 'text-amber-400'}>
          MPA: {eligibiliteData.resultat === 'tres_modeste' ? 'Tr&egrave;s Modeste' : eligibiliteData.resultat === 'modeste' ? 'Modeste' : eligibiliteData.resultat === 'non_eligible' ? 'Non &eacute;ligible' : '&Agrave; tester'}
        </span>
      </>
    )}
    {eligibiliteData?.typeBien && (
      <><span className="text-slate-500">|</span><span className="text-slate-400">{eligibiliteData.typeBien === 'maison' ? 'Maison' : 'Appart.'}</span></>
    )}
    {eligibiliteData?.statutLogement && (
      <><span className="text-slate-500">|</span><span className="text-slate-400">{eligibiliteData.statutLogement === 'proprietaire' ? 'Proprio.' : 'Locataire'}</span></>
    )}
    {eligibiliteData?.zone && (
      <><span className="text-slate-500">|</span><span className="text-slate-400">{eligibiliteData.zone === 'idf' ? 'IDF' : 'Province'}</span></>
    )}
  </div>
);

const SearchClientBlock = ({ searchQuery, setSearchQuery, searchResults, showSearchResults, searchLoading, clientExistant, setClientExistant, selectClientExistant, ouvrirFicheClient, variant }) => (
  <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4">
    <h4 className="text-blue-400 font-bold mb-3 flex items-center gap-2">
      <Search className="w-4 h-4" />Recherche client existant
    </h4>
    <p className="text-sm text-slate-400 mb-3">Tapez un t&eacute;l&eacute;phone, nom, pr&eacute;nom ou email pour v&eacute;rifier si le client existe</p>
    <div className="relative">
      <input type="text" value={searchQuery} data-testid="search-client-input"
        onChange={e => { setSearchQuery(e.target.value); setClientExistant(null); }}
        className="w-full px-4 py-3 bg-slate-900 border border-slate-600 rounded-xl text-white text-lg pr-12"
        placeholder="Telephone, nom, prenom ou email..." />
      {searchLoading && <Loader2 className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-blue-400 animate-spin" />}
    </div>
    
    {showSearchResults && searchResults.length > 0 && !clientExistant && (
      <div className="mt-3 bg-slate-900 border border-blue-500/50 rounded-xl overflow-hidden">
        <div className="px-3 py-2 bg-blue-500/20 border-b border-blue-500/30">
        <span className="text-sm text-blue-400 font-medium">{searchResults.length} client(s) trouv&eacute;(s)</span>
        </div>
        <div className="max-h-48 overflow-y-auto">
          {searchResults.map(client => (
            <div key={client.row_number} className="p-3 border-b border-slate-700 last:border-b-0 hover:bg-slate-800/50">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white font-medium">
                    {client.nom} {client.prenom}
                    {client.match_type && <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-slate-700 text-slate-300">via {client.match_type}</span>}
                  </p>
                  <p className="text-sm text-slate-400">{client.telephone || 'Tel N/C'} - {client.email || 'Email N/C'}</p>
                  <p className="text-xs text-slate-500 mt-1">{client.ville || 'Ville N/C'} - Etape: <span className="text-amber-400">{client.etape || 'N/C'}</span></p>
                </div>
                <div className="flex gap-2">
                  {variant === 'lead' && (
                    <button onClick={() => ouvrirFicheClient(client)} className="px-3 py-2 bg-emerald-600/20 hover:bg-emerald-600/30 text-emerald-400 rounded-lg text-sm flex items-center gap-1">
                      <ExternalLink className="w-4 h-4" />Ouvrir fiche
                    </button>
                  )}
                  <button onClick={() => selectClientExistant(client)} data-testid={`select-client-${client.row_number}`}
                    className="px-3 py-2 bg-blue-600/20 hover:bg-blue-600/30 text-blue-400 rounded-lg text-sm flex items-center gap-1">
                    <UserCheck className="w-4 h-4" />{variant === 'lead' ? 'Continuer' : 'Selectionner'}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    )}
    
    {clientExistant && (
      <div className="mt-3 p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-xl">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <UserCheck className="w-5 h-5 text-emerald-400" />
            <div>
              <p className="text-emerald-400 font-medium">Client existant s&eacute;lectionn&eacute;</p>
              <p className="text-sm text-white">{clientExistant.nom} {clientExistant.prenom} - {clientExistant.telephone}</p>
            </div>
          </div>
          {variant === 'lead' && (
            <button onClick={() => ouvrirFicheClient(clientExistant)} className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg text-sm flex items-center gap-2">
              <ExternalLink className="w-4 h-4" />Ouvrir sa fiche
            </button>
          )}
        </div>
      </div>
    )}
    
    {searchQuery.length >= 3 && !searchLoading && searchResults.length === 0 && !clientExistant && (
      <div className="mt-3 p-3 bg-amber-500/10 border border-amber-500/30 rounded-xl flex items-center gap-2">
        <AlertTriangle className="w-5 h-5 text-amber-400" />
        <span className="text-amber-400 text-sm">
          {variant === 'lead' ? 'Aucun client trouv&eacute; → Nouveau lead' : 'Aucun client trouv&eacute; → Les infos seront enregistr&eacute;es pour stats uniquement'}
        </span>
      </div>
    )}
  </div>
);

export default QualificationAppel;
